import { BlockUI } from 'primereact/blockui';
import { ProgressBar } from 'primereact/progressbar';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { RoundedFilledButton } from '@/components/buttons/Buttons';
import Dialog from '@/components/dialog/Dialog';
import DialogHeader from '@/components/dialog/DialogHeader';
import IdGeneratorService from '@/services/id-generator.service';
import styles from './single-add.module.css';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useQuery } from '@tanstack/react-query';
import FlagsModel, { Flag } from '@/modules/authentication/sign-up/components/CountryFlags';
import { SelectCountryIconDropdown } from '@/components/form-inputs/select-country/SelectCountryIconDropdown';
import { People } from '@/modules/people/engine/People';
import { yupResolver } from '@hookform/resolvers/yup';
import { InputController } from '@/components/inputs/InputController';
import TextInput from '@/components/inputs/TextInput';
import { useAuthenticationStore } from '@/store';
import { X4bUserRoleEnum } from '@/constants/roles';

type Props = {
	Id: number;
};

export const SingleAddDialogModal = NiceModal.create<Props>((props: Props) => {
	return (
		<SingleAddDialog {...props} />
	);
});



const SingleAddDialog = (props: Props) => {
	// TODO: change formId this to be in-line with how the
	// LoginForm works once implementation has been started.
	const formId = 'single-add';
	//const form = useForm<EmployeeDto>();
	const form = useForm<People.EmployeeForm>({
		resolver: yupResolver(People.employeeFormSchema),
		mode: 'all',
		// Onboarding dialog has no Role UI; default to User so the now-required field is satisfied.
		defaultValues: { RoleId: X4bUserRoleEnum.User },
	});
	const [formBlocked, setFormBlocked] = useState(false);
	const [selectedCountry, setSelectedCountry] = useState<Flag | null>({
		name: 'Philippines',
		code: 'PH',
	} as Flag);
	const isManagerOrAdmin = useAuthenticationStore((state) => state.hasRoles)([X4bUserRoleEnum.Admin, X4bUserRoleEnum.Manager]);

	var modal = useModal();

	const isAdd = props.Id == 0;

	const employeeQ = useQuery({
		queryKey: ['SingleEditPeopleForm.employee', props.Id],
		queryFn: async () => {
			return await People.getEmployeeForm(props.Id);
		},
		enabled: props.Id !== 0,
	});

	useEffect(() => {
		if (employeeQ.data) {
			form.reset(employeeQ.data);
		}
	}, [employeeQ.data, form]);


	const errors = form.formState.errors;
	return (
		<Dialog
			header={
				<BlockUI blocked={formBlocked} className={styles.formBlock}>
					<DialogHeader title="Single Add" />
				</BlockUI>
			}
			className="tablet:!w-[calc(100vw-6rem)] tablet:!max-w-[calc(100vw-6rem)]"
			visible={true}
			onHide={() => {
				modal.remove();
			}}
			pt={{
				content: () => ({
					className: styles.content,
				}),
				closeButton: () => ({
					className: styles.closeButton,
				}),
			}}
		>
			<ProgressBar
				mode="indeterminate"
				color="#0080FF"
				className={`${formBlocked ? '' : 'hidden'} ${styles.progressBar}`}
			/>
			<BlockUI
				blocked={formBlocked}
				className={styles.formBlock}
				template="Sending Invite..."
			>
				<p>
					Invite your team to join your company's account. For a
					faster activation experience, add a phone number. This
					allows Xpress to send the invitation directly through the
					app and/or by SMS if the phone number is associated with an
					Xpress account.
				</p>

				<form
					id={new IdGeneratorService(formId).formId}
					action=""
					className={styles.addDialogForm}
					onSubmit={(e) => e.preventDefault()}
				>
					<div className={`${styles.inputGroup} p-inputgroup p-input-filled`}>
						<InputController id="FirstName" control={form.control} rules={{ required: 'Field is required' }}>
							<TextInput label="First Name*" />
						</InputController>

						<InputController id="LastName" control={form.control} rules={{ required: 'Field is required' }}>
							<TextInput label="Last Name*" />
						</InputController>
					</div>
					<div
						className={`${styles.inputGroup} p-inputgroup p-input-filled`}
					>
						<InputController id="Email" control={form.control} rules={{ required: 'Field is required' }}>
							<TextInput label="Email*" />
						</InputController>
						<InputController id="ReferenceCode" control={form.control}>
							<TextInput label="ID" />
						</InputController>
					</div>
					<p className={styles.requiredLabel}>
						Required form labels ends with an *
					</p>
					<hr className={styles.addSeparator} />
					<div className={styles.secondaryHeading}>
						<h2>Add a Mobile Number</h2>
						<span>(Recommended)</span>
					</div>
					<p>
						Experience faster activation by adding a mobile number.
						If the number is associated with a Xpress account,
						Xpress will send an invite directly via the Xpress app
						and/or text messages.
					</p>
					<div className={`${styles.phoneGroup} p-inputgroup p-input-filled`}>
						<Controller
							name={'Country'}
							control={form.control}
							defaultValue={FlagsModel.flagByCountryCode('PH')}
							render={({ field }) => (<SelectCountryIconDropdown value={field.value} onChange={field.onChange} />)}
						/>


						<InputController id="Phone" control={form.control}>
							<TextInput
								label="Phone Number*"
								keyfilter="num"
								containerCls="w-[200px]"
							/>
						</InputController>
					</div>
					{isManagerOrAdmin && (
						<RoundedFilledButton
							id="send-invite"
							formName={formId}
							label={isAdd ? "Send Invite" : "Update"}
							className="text-lg font-poppins h-[48px]"
							onClick={
								form.handleSubmit(async (data: People.EmployeeForm) => {
									setFormBlocked(true);
									try {
										await People.sendEmployeeForm(data, isAdd);
										modal.resolve();
										modal.remove();
										// await props.onAdded();
									} finally {
										setFormBlocked(false);
									}
								}, (err) => {
									console.log(err);
								})
							}
							type="submit"
						/>)}
				</form>
			</BlockUI>
			<p>
				If you add a phone number or email address associated with
				Xpress account, Xpress will also send an invite directly via the
				Xpress app and/or text messages (in addition to email).
			</p>
		</Dialog>
	);
};

export default SingleAddDialog;
